import moment from 'moment';
import {
  getFundsVerificationMessage,
  getFundsVerificationSubject
} from '../constants';

export const AddRequiredPropsToRequests = (properties, request) => {
  return {
    ...request,
    ...properties
  };
};

const addAdditionalDoc = (requestDocuments, selectedDocument) => {
  const {
    customerRequestId,
    customerId,
    vehicleId,
    purchaseId
  } = selectedDocument;
  const additionalDoc = [
    {
      id: requestDocuments.length + 1,
      customerId,
      customerRequestId,
      vehicleId,
      title: 'Additional document',
      purchaseId,
      status: 'Open',
      description:
        '1 additional address verification document from List A below.'
    }
  ];

  const readyToUpload = requestDocuments.filter(x => x.upload);

  if (readyToUpload.length === requestDocuments.length)
    return requestDocuments.concat(additionalDoc);

  return requestDocuments;
};

export const SetRequestDocumentsForCIA = requestDocuments => {
  const requestDocument1 = requestDocuments[0] || {};
  const requestDocument2 = requestDocuments[1] || {};

  return [
    {
      ...requestDocument1,
      title: "Front of Cashier's Check",
      description: null
    },
    {
      ...requestDocument2,
      id: requestDocument2?.id || requestDocuments.length + 1,
      title: "Back of Cashier's Check",
      description: null,
      requestType: requestDocuments[0]?.requestType,
      purchaseId: requestDocuments[0]?.purchaseId
    }
  ];
};

export const SetRequestDocumentsForFV = requestDocuments => {
  const requestDocument = requestDocuments[0];
  return [
    {
      ...requestDocument,
      title: 'Link your bank account',
      description: null
    }
  ];
};

export const SetRequestDocumentProps = (
  state,
  payload,
  addAdditionalDocument
) => {
  const { requestDocuments } = state;
  let updatedRequestDocuments = [];
  const selectedDocument = requestDocuments.find(x => x.id === payload?.id);
  if (!selectedDocument) return requestDocuments;

  updatedRequestDocuments = requestDocuments.map(x => {
    if (x.id !== payload.id) return x;
    return { ...x, ...payload };
  });

  if (addAdditionalDocument)
    updatedRequestDocuments = addAdditionalDoc(
      requestDocuments,
      selectedDocument
    );

  return [...updatedRequestDocuments];
};

export const SetRequestDocumentStatus = (requestDocument, request) => {
  return {
    ...requestDocument,
    status: requestDocument.status || request.status
  };
};

const setUploadedRequestDocumentProps = (state, payload) => {
  const {
    additionalInformation: { subject, message },
    status,
    customerId,
    customerRequestId,
    vehicleId
  } = payload || {};
  return payload.documentIds.map(x => {
    return {
      id: x,
      customerRequestId,
      title: subject,
      description: message,
      customerId,
      vehicleId,
      documentId: x,
      completed: true,
      status
    };
  });
};

const newRequestDocumentList = (state, payload) => {
  const {
    additionalInformation: { subject, message },
    customerId,
    customerRequestId,
    vehicleId,
    purchaseId,
    type,
    status
  } = payload || {};
  return [
    {
      id: customerRequestId,
      customerRequestId,
      title: subject,
      description: message,
      customerId,
      vehicleId,
      purchaseId,
      requestType: type,
      status
    }
  ];
};

export const SetRequestDocuments = (state, payload) => {
  if (payload?.status === 'Fulfilled') {
    return setUploadedRequestDocumentProps(state, payload);
  }

  return newRequestDocumentList(state, payload);
};

export const SetRequestDocumentIsFulfilled = (doc, request) => {
  if (request.status === 'Fulfilled') {
    return {
      ...doc,
      isFulfilled: true
    };
  }

  return {
    ...doc,
    isFulfilled: false
  };
};

export const SetRequestIsFulfilled = request => {
  if (request.status === 'Fulfilled') {
    return {
      ...request,
      isFulfilled: true
    };
  }

  return {
    ...request,
    isFulfilled: false
  };
};

export const SetRequestDocumentTitle = requestDocument => {
  // eslint-disable-next-line prefer-const
  let { title, upload } = requestDocument;

  if (title === 'Additional document' && upload)
    title = requestDocument.image.name;

  return {
    ...requestDocument,
    title
  };
};

const getDeadlineWithOffset = (deadline, offset) => {
  return moment(deadline).add(moment.duration(offset));
};

const formatDeadline = (deadlineWithOffset, crType, deliveryType) => {
  const completeByDate = deadlineWithOffset.format('ddd MMM D, YYYY');
  const completeByTime = deadlineWithOffset.format('h:mm a');
  const formattedDeliveryType = deliveryType?.toLowerCase()?.includes('pickup')
    ? 'Pickup'
    : 'Delivery';

  return (crType === 'Insurance' || crType === 'Contracts') && deliveryType
    ? `Complete before your ${formattedDeliveryType}`
    : `Complete by ${completeByDate} at ${completeByTime} `;
};

export const SetDeadline = (request, orderSummary) => {
  const { customDeadline: { kind, deadline, offset } = {}, type } = request;
  const {
    orderPlacedOnUtc,
    schedule: { scheduleStartDateTimeUtc, deliveryType } = {}
  } = orderSummary;
  let deadlineWithOffset;
  let deadlineFormatted;
  let deadlineSort;

  switch (kind?.toLowerCase()) {
    case 'orderplaced':
      deadlineWithOffset = getDeadlineWithOffset(orderPlacedOnUtc, offset);
      deadlineFormatted = formatDeadline(
        deadlineWithOffset,
        type,
        deliveryType
      );
      deadlineSort = new Date(deadlineWithOffset.format());
      break;
    case 'delivery':
      deadlineWithOffset = getDeadlineWithOffset(
        scheduleStartDateTimeUtc,
        offset
      );
      deadlineFormatted = formatDeadline(
        deadlineWithOffset,
        type,
        deliveryType
      );
      deadlineSort = new Date(deadlineWithOffset.format());
      break;
    case 'asap':
      deadlineWithOffset = getDeadlineWithOffset(deadline, offset);
      deadlineFormatted = 'ASAP';
      deadlineSort = new Date(0);
      break;
    case 'pickup':
      deadlineWithOffset = getDeadlineWithOffset(
        scheduleStartDateTimeUtc,
        offset
      );
      deadlineFormatted = formatDeadline(
        deadlineWithOffset,
        type,
        deliveryType
      );
      deadlineSort = new Date(deadlineWithOffset.format());
      break;
    default:
      deadlineWithOffset = getDeadlineWithOffset(deadline, offset);
      deadlineFormatted = deadline.includes('0001-01')
        ? 'ASAP'
        : formatDeadline(deadlineWithOffset, type, deliveryType);
      deadlineSort = new Date(deadlineWithOffset.format());
      break;
  }

  return {
    ...request,
    deadline: deadlineFormatted,
    sortable: deadlineSort
  };
};

export const SetFundsVerificationRequestProperties = (
  request,
  verifications,
  paymentDetails
) => {
  const { type } = request;
  const {
    customerIsGreen,
    fundsVerification: {
      customerHasPlaid,
      isRequired,
      isCompleted,
      isPlaidValidForFundsVerification,
      plaidContainsAchAccount,
      accountNumberMask
    } = {}
  } = verifications;
  const { dueAtOrder: { displayValue } = {} } = paymentDetails;
  const fundsAreVerified = customerIsGreen || (isRequired && isCompleted);
  const subject = getFundsVerificationSubject(displayValue);
  const message = getFundsVerificationMessage(
    displayValue,
    accountNumberMask
  );

  if (type !== 'FundsVerification')
    return {
      ...request
    };

  if (!fundsAreVerified && isPlaidValidForFundsVerification)
    return {
      ...request,
      customStatus: {
        message: 'insufficientFunds',
        status: 'warning'
      },
      additionalInformation: {
        ...request?.additionalInformation,
        subject,
        message
      }
    };

  if (customerHasPlaid && !plaidContainsAchAccount)
    return {
      ...request,
      customStatus: {
        message: 'wrongAccountNumber',
        status: 'warning'
      },
      additionalInformation: {
        ...request?.additionalInformation,
        subject,
        message
      }
    };

  if (fundsAreVerified && request?.status === 'Open')
    return {
      ...request,
      status: 'Pending',
      customStatus: {
        message: 'Pending',
        status: 'info'
      },
      additionalInformation: {
        ...request?.additionalInformation,
        subject,
        message
      }
    };

  return {
    ...request,
    additionalInformation: {
      ...request?.additionalInformation,
      subject,
      message
    }
  };
};

export const UpdateRequestDocument = (state, payload) => {
  const { requestDocuments } = state;
  const newRequestDocuments = requestDocuments?.map(document => {
    if (document.id !== payload.id) return document;
    return {
      ...document,
      image: payload?.image,
      status: payload?.status,
      subtitle: payload.subtitle,
      title: payload.title
    };
  });

  return newRequestDocuments;
};
